import {useState} from 'react'
import {Play} from 'lucide-react'
import {useI18n} from '@/i18n'
import {formatTimestamp, formatRelativeTime} from '@/lib/date-utils'
import {Button} from '@/components/ui/button'
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {DetailsCard} from '@/components/shared/DetailsCard'
import {useSyncRuns} from '@/features/inbound-connectors/hooks/useSyncRunQueries'
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip'
import {PreviewResultDialog} from '@/components/inbound-connectors/PreviewResultDialog'
import {InboundConnectorType, SyncType} from '@/types/inbound-connector'
import type {
    InboundConnector,
    PreviewResponse,
    SyncStatus,
} from '@/types/inbound-connector'
import {ButtonSpinner} from '@/components/shared/ButtonSpinner'
import {CodeText} from '@/components/shared/CodeText'
import {MutedText} from '@/components/shared/MutedText'
import {EntityLink} from '@/components/shared/EntityLink'
import {Icon} from '@/components/shared/Icon'
import {InlineRow} from '@/components/shared/InlineRow'
import {Stack} from '@/components/shared/Stack'
import type {UseMutationResult} from '@tanstack/react-query'
import type {SyncTriggerResponse} from '@/types/inbound-connector'

const LATEST_RUN_QUERY = 'page=0&size=1&sort=startedAt,desc'

export function connectorTypeLabel(type: string, i18n: ReturnType<typeof useI18n>): string {
    switch (type) {
        case InboundConnectorType.REST:
            return i18n.inboundConnectors.typeRest
        case InboundConnectorType.SCIM_2_0:
            return i18n.inboundConnectors.typeScim
        case InboundConnectorType.SCRIPT:
            return i18n.inboundConnectors.typeScript
        default:
            return type
    }
}

export function syncTypeLabel(type: string, i18n: ReturnType<typeof useI18n>): string {
    switch (type) {
        case SyncType.FULL:
            return i18n.inboundConnectors.syncTypeFull
        case SyncType.PREVIEW:
            return i18n.inboundConnectors.syncTypePreview
        default:
            return type
    }
}

export interface OverviewSubPageProps {
    connector: InboundConnector
    syncStatus: SyncStatus | undefined
    canExecute: boolean
    triggerMutation: UseMutationResult<SyncTriggerResponse, Error, 'full'>
    deltaSyncMutation: UseMutationResult<SyncTriggerResponse, Error, void>
    previewMutation: UseMutationResult<PreviewResponse, Error, void>
    onTriggerSync: (syncType: 'full') => void
    onTriggerDeltaSync: () => void
    onRunPreview: () => void
    onPreviewClose: () => void
    previewOpen: boolean
    previewResponse: PreviewResponse | null
    lastSyncCreatedCountTestId?: string
    lastSyncUpdatedCountTestId?: string
    lastSyncErroredCountTestId?: string
}

// WHY: the three inbound connector types that support an incremental (delta)
// sync path on the SPI surface. The button only renders when the connector
// reports one of these types (issue 736 B.4/B.6/B.10).
const DELTA_SYNC_SUPPORTED_TYPES: ReadonlySet<string> = new Set([
    InboundConnectorType.REST,
    InboundConnectorType.SCRIPT,
    InboundConnectorType.SAP_HCM,
])

export function supportsDeltaSync(connectorType: string): boolean {
    return DELTA_SYNC_SUPPORTED_TYPES.has(connectorType)
}

export function OverviewSubPage({
    connector,
    syncStatus,
    canExecute,
    triggerMutation,
    deltaSyncMutation,
    previewMutation,
    onTriggerSync,
    onTriggerDeltaSync,
    onRunPreview,
    onPreviewClose,
    previewOpen,
    previewResponse,
    lastSyncCreatedCountTestId,
    lastSyncUpdatedCountTestId,
    lastSyncErroredCountTestId,
}: OverviewSubPageProps) {
    const i18n = useI18n()
    const [syncDialogOpen, setSyncDialogOpen] = useState<'full' | 'delta' | null>(null)
    const deltaSyncEnabled = supportsDeltaSync(connector.connectorType)

    const latestRunQuery = useSyncRuns(connector.id, LATEST_RUN_QUERY)
    const latestRun = latestRunQuery.data?.content?.[0]

    const isRunning = syncStatus?.running ?? false
    const triggerDisabledReason = !connector.enabled
        ? i18n.inboundConnectors.enableFirst
        : isRunning
            ? i18n.inboundConnectors.syncAlreadyRunning
            : null
    const previewDisabledReason = !connector.enabled ? i18n.inboundConnectors.enableFirst : null

    return (
        <Stack gap="lg" className="mt-4">
            <DetailsCard
                data-testid="connector-info-card"
                title={i18n.inboundConnectors.connectorInfo}
                fields={[
                    {
                        label: i18n.inboundConnectors.targetSystem,
                        value: <EntityLink value={connector.targetSystem} testId="target-system-link"/>,
                    },
                    {label: i18n.inboundConnectors.connectorType, value: connectorTypeLabel(connector.connectorType, i18n)},
                    {label: i18n.inboundConnectors.description, value: <MutedText>{connector.description || i18n.inboundConnectors.noDescription}</MutedText>},
                    {label: i18n.inboundConnectors.createdAt, value: formatTimestamp(connector.createdAt)},
                    {label: i18n.inboundConnectors.updatedAt, value: formatTimestamp(connector.updatedAt)},
                ]}
            />

            <DetailsCard
                data-testid="schedule-card"
                title={i18n.inboundConnectors.scheduleSection}
                fields={[
                    {label: i18n.inboundConnectors.cronExpression, value: <CodeText>{connector.cronExpression}</CodeText>},
                ]}
            />

            <DetailsCard
                data-testid="last-sync-card"
                title={i18n.inboundConnectors.lastSyncSection}
                fields={[
                    {
                        label: i18n.inboundConnectors.lastSyncSection,
                        value: connector.lastSyncAt
                            ? <TooltipProvider><Tooltip><TooltipTrigger asChild><span>{formatRelativeTime(connector.lastSyncAt)}</span></TooltipTrigger><TooltipContent>{formatTimestamp(connector.lastSyncAt)}</TooltipContent></Tooltip></TooltipProvider>
                            : <MutedText>{i18n.inboundConnectors.neverSynced}</MutedText>,
                    },
                    {
                        label: i18n.inboundConnectors.syncRunCreated,
                        value: <span data-testid={lastSyncCreatedCountTestId}>{latestRun ? latestRun.recordsCreated : '—'}</span>,
                    },
                    {
                        label: i18n.inboundConnectors.syncRunUpdated,
                        value: <span data-testid={lastSyncUpdatedCountTestId}>{latestRun ? latestRun.recordsUpdated : '—'}</span>,
                    },
                    {
                        label: i18n.inboundConnectors.syncRunErrored,
                        value: <span data-testid={lastSyncErroredCountTestId}>{latestRun ? latestRun.recordsErrored : '—'}</span>,
                    },
                ]}
            />

            {canExecute && (
                <Card data-testid="manual-sync-card">
                    <CardHeader>
                        <CardTitle data-testid="card-title-manual-sync">{i18n.inboundConnectors.manualSync}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <InlineRow wrap gap="lg">
                            <TriggerButton
                                label={i18n.inboundConnectors.runFullSync}
                                variant="default"
                                disabledReason={triggerDisabledReason}
                                isPending={triggerMutation.isPending}
                                onClick={() => setSyncDialogOpen('full')}
                                testId="btn-sync-full"
                            />
                            {deltaSyncEnabled && (
                                <TriggerButton
                                    label={i18n.inboundConnectors.runDeltaSync}
                                    variant="secondary"
                                    disabledReason={triggerDisabledReason}
                                    isPending={deltaSyncMutation.isPending}
                                    onClick={() => setSyncDialogOpen('delta')}
                                    testId="btn-sync-delta"
                                />
                            )}
                            <TriggerButton
                                label={i18n.inboundConnectors.runPreview}
                                variant="outline"
                                disabledReason={previewDisabledReason}
                                isPending={previewMutation.isPending}
                                onClick={onRunPreview}
                                testId="btn-sync-preview"
                            />
                        </InlineRow>
                    </CardContent>
                </Card>
            )}

            <Dialog open={syncDialogOpen !== null} onOpenChange={() => setSyncDialogOpen(null)}>
                <DialogContent data-testid="sync-confirm-dialog">
                    <DialogHeader>
                        <DialogTitle>{i18n.inboundConnectors.confirmSync}</DialogTitle>
                        <DialogDescription>
                            {syncDialogOpen === 'full' && i18n.inboundConnectors.syncFullConfirmation}
                            {syncDialogOpen === 'delta' && i18n.inboundConnectors.syncDeltaConfirmation}
                        </DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="sync"
                        onCancel={() => setSyncDialogOpen(null)}
                        saving={triggerMutation.isPending || deltaSyncMutation.isPending}
                        saveLabel={i18n.common.confirm}
                        saveType="button"
                        onSave={() => {
                            if (syncDialogOpen === 'full') {
                                onTriggerSync('full')
                            } else if (syncDialogOpen === 'delta') {
                                onTriggerDeltaSync()
                            }
                            setSyncDialogOpen(null)
                        }}
                    />
                </DialogContent>
            </Dialog>

            {previewOpen && (
                <PreviewResultDialog
                    connectorId={connector.id}
                    response={previewResponse}
                    isLoading={previewMutation.isPending}
                    onClose={onPreviewClose}
                />
            )}
        </Stack>
    )
}

function TriggerButton({label, variant, disabledReason, isPending, onClick, testId}: {
    label: string
    variant: 'default' | 'secondary' | 'outline'
    disabledReason: string | null
    isPending: boolean
    onClick: () => void
    testId: string
}) {
    const button = (
        <Button
            data-testid={testId}
            variant={variant}
            disabled={!!disabledReason || isPending}
            onClick={onClick}
        >
            {isPending
                ? <ButtonSpinner/>
                : <Icon icon={Play} className="mr-2"/>
            }
            {label}
        </Button>
    )

    if (disabledReason) {
        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span>{button}</span>
                    </TooltipTrigger>
                    <TooltipContent>{disabledReason}</TooltipContent>
                </Tooltip>
            </TooltipProvider>
        )
    }

    return button
}
